import React from "react";
import {
  MessageSquare, Users, Hash, Stethoscope, ShoppingCart,
  Coffee, Link2, FileText, ShieldCheck, Search, Activity, Sparkles,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, ConversationThread, FlowDiagram, FeatureCallout,
  SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogInternalHubChartContext = () => (
  <RichBlogLayout
    category="Practice Efficiency"
    eyebrow="Team Coordination"
    title="Internal Messaging That Lives Inside the Chart, Not Next to It"
    subtitle="Slack-style channels, but every @mention is one click from the patient's full chart. The Slack-and-PMS dance is over."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Lead>
      The hygienist messages the front desk: "What's going on with Maria's
      crown?" The front desk opens Slack to read the message, opens the PMS to
      look up Maria, opens the ledger to check the balance, switches back to
      Slack to reply. The patient is sitting in the chair while four tabs do a
      slow dance.
    </Lead>

    <Prose>
      <p>
        It feels modern, because Slack is modern. But the moment a teammate
        asks a question that requires the chart, the messaging tool stops
        helping and becomes a context-switching tax. Every "which Maria?" and
        "can you send me her chart number?" is a few seconds of friction that
        adds up across a full day of coordination.
      </p>
      <p>
        Asha rolls the messaging layer back into the system where the patient
        data already lives. The channels look familiar — morning huddle,
        hygiene team, supply orders, all staff, plus DMs — but every message
        is wired into chart context. An @mention isn't a notification, it's a
        link to the part of the chart your teammate needs.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 0, label: "Tabs to switch between Slack and the PMS" },
        { value: 1, label: "Click from a message to the patient's chart" },
        { value: 100, suffix: "%", label: "Messages tied to the clinical record" },
        { value: 5, label: "Channels prebuilt for how a real practice runs" },
      ]}
    />

    <Prose>
      <h2>The Slack-and-PMS tax</h2>
      <p>
        The cost of running messaging in a separate system isn't usually a
        single, visible event. It's the dozens of tiny ones a day — the
        re-explained patient, the screenshot of an x-ray pasted into a chat,
        the "let me grab the chart number" pause. None of these are huge.
        Together, they are a meaningful share of why coordination feels heavy.
      </p>
      <p>
        Patient details get retyped. Conversations end without resolution
        because the next thing required to act on them lives somewhere else.
        And the worst part: nothing said in chat ever makes it into the
        actual chart, so there is no record of why a decision was made when
        the same question comes up two weeks later.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Slack + PMS, separately"
      afterTitle="Messaging inside the chart"
      beforeItems={[
        "Discussion in Slack, decision in the PMS, chart never updated",
        "@mentions are notifications — context still requires a tab switch",
        "Patient identifiers get pasted into chat, often imprecisely",
        "Two-week-old conversations are unsearchable when a question recurs",
      ]}
      afterItems={[
        "Channels live in the same environment as the patient data",
        "@mentions deep-link to the exact chart, ledger, or appointment",
        "Discussion is automatically tied to the patient record",
        "Searchable, auditable history that travels with the chart",
      ]}
    />

    <Prose>
      <h2>The five rooms a practice actually needs</h2>
      <p>
        Most practices spend the first month of any messaging tool figuring
        out how to organize it. Asha skips that step. The hub ships with the
        channels real practices already run on.
      </p>
    </Prose>

    <ParallelGrid
      title="Channels prebuilt for the way a practice operates"
      columns={3}
      items={[
        { title: "Morning huddle", subtitle: "Pre-shift coordination", icon: Coffee, status: "active" },
        { title: "Hygiene team", subtitle: "RDH workflows + handoffs", icon: Stethoscope, status: "active" },
        { title: "Supply orders", subtitle: "Inventory + restock requests", icon: ShoppingCart, status: "active" },
        { title: "All staff", subtitle: "Practice-wide announcements", icon: Users, status: "active" },
        { title: "Direct messages", subtitle: "1:1 between teammates", icon: MessageSquare, status: "active" },
        { title: "Channel + chart linked", subtitle: "Every thread is searchable", icon: Hash, status: "done" },
      ]}
    />

    <Prose>
      <p>
        Notice that none of these are organized by tool. They're organized by
        how the work actually moves through the day. A morning huddle channel
        belongs next to today's schedule. A hygiene team channel belongs next
        to the hygiene chart. Supply orders belong next to the inventory
        ledger. The grouping is operational, not technological.
      </p>

      <h2>What an @mention actually does here</h2>
      <p>
        In Slack, an @mention pings a teammate. In the Asha hub, it does that
        too — and brings the chart with it. Teammates see the patient,
        appointment, and any relevant ledger context inline, without leaving
        the conversation.
      </p>
    </Prose>

    <ConversationThread
      title="Hygiene team channel · Maria L · 9:42 AM"
      channel="Internal hub"
      messages={[
        { from: "system", text: "#hygiene-team · 4 teammates online" },
        { from: "agent", text: "@front-desk Maria L is here for her 10AM hygiene — she mentioned the crown seat is bothering her. Can someone check the post-op note from last week?" },
        { from: "patient", text: "Sure, opening her chart now. Last note from Dr. Patel says minor occlusal high spot — she was supposed to come back if it didn't settle." },
        { from: "agent", text: "Got it. I'll have Dr. Patel pop in after the prophy. Adding a 10-min adjustment to her appointment." },
        { from: "patient", text: "Done — chart updated, schedule shifted by 10. Adjusted billing on the encounter so it's not a separate appointment." },
        { from: "system", text: "Decision logged on Maria L's chart · linked to original crown encounter" },
      ]}
    />

    <Prose>
      <p>
        Three teammates, three taps, one chart updated. Nobody copied a
        patient ID. Nobody pasted a screenshot. The conversation became part
        of the record automatically, so when the question comes up at the
        next morning huddle, the answer is one search away — not buried in
        Slack DMs from last Tuesday.
      </p>
    </Prose>

    <PullQuote>
      An @mention isn't a notification. It's a deep link to the part of the
      chart your teammate needs.
    </PullQuote>

    <Prose>
      <h2>How chart context gets attached</h2>
      <p>
        Messaging-in-the-chart isn't an integration. There is no Slack-to-PMS
        bridge, no two-way sync, no "linked apps" page. The hub and the chart
        are the same surface, which is why context flows automatically rather
        than via webhook.
      </p>
    </Prose>

    <FlowDiagram
      title="From a message to a chart action"
      nodes={[
        { label: "Teammate posts in channel", sublabel: "Hygiene · supply · huddle", icon: MessageSquare },
        { label: "@mention resolves to a person + patient", sublabel: "Inline chart preview", icon: Link2 },
        { label: "One click to chart, ledger, or note", sublabel: "Same environment", icon: FileText },
        { label: "Decision is logged", sublabel: "Searchable, auditable, attached", icon: ShieldCheck },
      ]}
    />

    <Prose>
      <h2>The morning huddle, rewritten</h2>
      <p>
        The classic morning huddle is a dry-erase board, a printed schedule,
        and someone reading the day out loud. With messaging in the chart,
        the huddle becomes a thread. The hygiene lead pre-flags the patients
        with active recall conversations. The doctor flags the long-case
        patient who needs a quiet operatory. The front desk flags the
        balance the office manager needs to handle.
      </p>
      <p>
        None of those flags are a separate document. They're messages, each
        one wired to the patient they describe. The huddle becomes
        searchable. The "wait, who was the one with the swelling?" question
        from yesterday becomes a Cmd+F instead of an investigation.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Pre-shift posts land in the morning huddle channel",
          body:
            "Each post is tied to a patient or a slot, so the team can scroll the day in real chart context.",
        },
        {
          title: "Mid-shift coordination flows through the same channels",
          body:
            "Supply requests, urgent provider questions, and patient handoffs happen without leaving the chart.",
        },
        {
          title: "Decisions are logged on the chart automatically",
          body:
            "What the team agreed to becomes part of the record — not a Slack thread that disappears in 90 days.",
        },
        {
          title: "End-of-day review is a searchable timeline",
          body:
            "Office manager can scroll the day's coordination in one place, attached to the patients it actually concerned.",
        },
      ]}
    />

    <Prose>
      <h2>Why this changes the speed of the practice</h2>
      <p>
        The teams that adopt this stop noticing it after about a week. That's
        the tell that it's working. The previous workflow — switch tabs, find
        the chart, retype the question, send the message — gets replaced by
        a single action that was the only action you ever wanted in the
        first place.
      </p>
      <p>
        It also lowers the cost of a new teammate joining. They learn the
        channels, the people, and the chart in the same surface. There's no
        "oh, we use Slack for this part and the PMS for that part" lecture.
        It's all one place.
      </p>
    </Prose>

    <FeatureCallout
      stat="One surface"
      label="Messaging, chart, and decisions in the same place"
      description="Stop paying the tax of switching between Slack and the PMS for every patient question. The hub is in the chart."
      icon={Sparkles}
    />

    <Prose>
      <p>
        The thing most practices don't realize is how much friction is hidden
        in their messaging stack. It looks like Slack is free. It isn't.
        It's a daily, distributed cost paid in tab switches, retyped
        identifiers, and decisions that never landed in the chart. Asha pays
        that cost back by collapsing the two surfaces into one.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Internal messaging shouldn't be a separate product. It should be the
        thing that happens around the chart, in the chart, with full chart
        context. When it is, coordination gets faster, decisions get
        recorded, and the dance between Slack and the PMS finally ends.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogInternalHubChartContext;
